/**
 * LiveKit data-channel sync for quiz push / submissions / grasp.
 *
 * Primary live path: room.localParticipant.publishData + RoomEvent.DataReceived
 * REST is only for persist + one-shot hydrate — not a poll loop.
 */

import { useEffect, useRef, useState } from "react";
import { useMaybeRoomContext } from "@livekit/components-react";
import { RoomEvent } from "livekit-client";
import {
  LIVE_GRASP_PROMPT_TYPE,
  LIVE_GRASP_TYPE,
  LIVE_QUIZ_PUSH_TYPE,
  LIVE_QUIZ_SUBMIT_TYPE,
  normalizeGraspVote,
  type ActiveLiveQuiz,
  type GraspVote,
  type LiveGraspMessage,
  type LiveGraspPrompt,
  type LiveGraspPromptMessage,
  type LiveQuizPushMessage,
  type LiveQuizSubmission,
  type LiveQuizSubmitMessage,
} from "./liveQuizTypes";

function encode(msg: object): Uint8Array {
  // ---------------------------------------------------------------
  // step 1: JSON-encode payload for publishData
  // ---------------------------------------------------------------
  return new TextEncoder().encode(JSON.stringify(msg));
}

function decode(data: Uint8Array): Record<string, unknown> | null {
  // ---------------------------------------------------------------
  // step 1: Decode LiveKit data bytes → JSON object
  // ---------------------------------------------------------------
  try {
    return JSON.parse(new TextDecoder().decode(data)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function mergeGraspVotes(prev: GraspVote[], incoming: GraspVote[]): GraspVote[] {
  // ---------------------------------------------------------------
  // step 1: Upsert by student×topic; keep richer subtopics/tags
  // ---------------------------------------------------------------
  const byKey = new Map<string, GraspVote>();
  const keyOf = (v: GraspVote) => `${v.student_id}::${v.topic_id}`;
  const score = (v: GraspVote) =>
    (v.unclear_subtopics?.length || 0) + (v.confusion_tags?.length || 0);
  for (const v of prev) byKey.set(keyOf(v), v);
  for (const v of incoming) {
    const k = keyOf(v);
    const existing = byKey.get(k);
    if (!existing || score(v) >= score(existing) || v.submitted_at >= existing.submitted_at) {
      byKey.set(k, {
        ...existing,
        ...v,
        unclear_subtopics:
          (v.unclear_subtopics?.length || 0) > 0
            ? v.unclear_subtopics
            : existing?.unclear_subtopics || [],
        confusion_tags:
          (v.confusion_tags?.length || 0) > 0
            ? v.confusion_tags
            : existing?.confusion_tags || [],
        message: v.message || existing?.message || null,
      });
    }
  }
  return Array.from(byKey.values());
}

export function useLiveQuizSync(options: {
  role: "trainer" | "student";
  enabled?: boolean;
}) {
  // ---------------------------------------------------------------
  // step 1: Optional room context (safe before LiveKitRoom mounts)
  // ---------------------------------------------------------------
  const room = useMaybeRoomContext();
  const [activeQuiz, setActiveQuiz] = useState<ActiveLiveQuiz | null>(null);
  const [submissions, setSubmissions] = useState<LiveQuizSubmission[]>([]);
  const [graspVotes, setGraspVotes] = useState<GraspVote[]>([]);
  const lastPushRef = useRef("");

  useEffect(() => {
    // ---------------------------------------------------------------
    // step 2: Subscribe to quiz / submission / grasp data messages
    // ---------------------------------------------------------------
    if (!options.enabled || !room) return;

    const onData = (payload: Uint8Array) => {
      const raw = decode(payload);
      if (!raw || raw.v !== 1) return;
      if (raw.type === LIVE_QUIZ_PUSH_TYPE && raw.quiz) {
        setActiveQuiz(raw.quiz as ActiveLiveQuiz);
      } else if (raw.type === LIVE_QUIZ_SUBMIT_TYPE && raw.submission) {
        const sub = raw.submission as LiveQuizSubmission;
        setSubmissions((prev) => {
          const rest = prev.filter(
            (s) =>
              !(
                s.student_id === sub.student_id &&
                s.push_id === sub.push_id
              ),
          );
          return [...rest, sub];
        });
        // All students submitted → end LIVE state immediately (server auto-complete)
        if (sub.quiz_completed) {
          setActiveQuiz((prev) =>
            prev && prev.push_id === sub.push_id && prev.status === "active"
              ? { ...prev, status: "completed" }
              : prev,
          );
        }
      } else if (raw.type === LIVE_GRASP_TYPE && raw.vote) {
        const vote = normalizeGraspVote(
          raw.vote as Record<string, unknown>,
        );
        if (!vote) return;
        setGraspVotes((prev) => mergeGraspVotes(prev, [vote]));
      }
    };

    room.on(RoomEvent.DataReceived, onData);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
    };
  }, [options.enabled, room]);

  const publishQuiz = async (quiz: ActiveLiveQuiz) => {
    // ---------------------------------------------------------------
    // step 3: Broadcast active quiz to room (trainer after REST push)
    // ---------------------------------------------------------------
    if (!room?.localParticipant) return;
    const msg: LiveQuizPushMessage = {
      type: LIVE_QUIZ_PUSH_TYPE,
      v: 1,
      quiz,
    };
    const key = quiz.push_id;
    if (key === lastPushRef.current) return;
    lastPushRef.current = key;
    setActiveQuiz(quiz);
    setSubmissions([]);
    await room.localParticipant.publishData(encode(msg), { reliable: true });
  };

  const publishSubmission = async (submission: LiveQuizSubmission) => {
    // ---------------------------------------------------------------
    // step 4: Broadcast student score to trainer report
    // ---------------------------------------------------------------
    if (!room?.localParticipant) return;
    const msg: LiveQuizSubmitMessage = {
      type: LIVE_QUIZ_SUBMIT_TYPE,
      v: 1,
      submission,
    };
    setSubmissions((prev) => {
      const rest = prev.filter(
        (s) =>
          !(
            s.student_id === submission.student_id &&
            s.push_id === submission.push_id
          ),
      );
      return [...rest, submission];
    });
    await room.localParticipant.publishData(encode(msg), { reliable: true });
  };

  const publishGrasp = async (vote: GraspVote) => {
    // ---------------------------------------------------------------
    // step 5: Broadcast grasp vote to trainer report
    // ---------------------------------------------------------------
    if (!room?.localParticipant) return;
    const normalized = normalizeGraspVote(vote) || vote;
    const msg: LiveGraspMessage = {
      type: LIVE_GRASP_TYPE,
      v: 1,
      vote: normalized,
    };
    setGraspVotes((prev) => mergeGraspVotes(prev, [normalized]));
    await room.localParticipant.publishData(encode(msg), { reliable: true });
  };

  const publishGraspPrompt = async (prompt: LiveGraspPrompt) => {
    // ---------------------------------------------------------------
    // step 5b: Ask students to open GraspFeedback (skip / no post-quiz)
    // ---------------------------------------------------------------
    if (!room?.localParticipant) return;
    const topicId = String(prompt.topic_id || "").trim();
    if (!topicId) return;
    const msg: LiveGraspPromptMessage = {
      type: LIVE_GRASP_PROMPT_TYPE,
      v: 1,
      prompt: {
        topic_id: topicId,
        topic_title: String(prompt.topic_title || "").trim(),
      },
    };
    await room.localParticipant.publishData(encode(msg), { reliable: true });
  };

  const replaceGraspVotes = (votes: GraspVote[]) => {
    // ---------------------------------------------------------------
    // step 6: REST hydrate — merge so LiveKit detail is not wiped
    // ---------------------------------------------------------------
    setGraspVotes((prev) => mergeGraspVotes(prev, votes));
  };

  return {
    activeQuiz,
    setActiveQuiz,
    submissions,
    setSubmissions,
    graspVotes,
    setGraspVotes: replaceGraspVotes,
    publishQuiz,
    publishSubmission,
    publishGrasp,
    publishGraspPrompt,
  };
}
